import Config from '@/config'
import { isApplePayAvailable } from '@/utils/applepay-detect'
import { getPhoneNumberByIsoCode2 } from '@/utils/phone-number'
import PAYMENT_TYPES from '@/constants/payment-types'
import CHECKOUT_STEPS from '@/constants/checkout-step'
import PAYMENT_CARD_TYPES from '@/constants/payment-card-types'

const getters = {
  token: (state) => state.auth.accessToken,
  checkoutId: (state) => state.session.checkoutId,
  deviceId: (state) => state.session.deviceId,
  sessionStatus: (state) => state.session.status,
  isValidSession: (state) => state.session.status === 'valid',
  kycSessionStatus: (state) => state.kyc.status,
  isDeepIntegration: (state) => state.application.deepIntegration,
  isShownPaymentPlan: (_, getters) => {
    if (
      getters['session/isPayInXOrder'] ||
      getters['session/fromPaymentLink']
    ) {
      return false
    }

    // TODO:: This is workaround. We will remove this after https://tamarapay.atlassian.net/browse/MVP-2176
    const merchantId = getters['session/merchantId']
    if (Config.paymentPlanInstallmentExcludeMerchantIds.includes(merchantId)) {
      return false
    }

    return (
      getters['session/isInstalmentOrder'] || getters['session/isNamshiOrder']
    )
  },
  customerPhoneNumber: (_, getters) => {
    if (getters['auth/customerPhone']?.phoneNumber) {
      return getters['auth/customerPhone']
    }

    return getPhoneNumberByIsoCode2(
      getters['auth/customerPhoneNumber'],
      getters['auth/isoCode2']
    )
  },
  logoName: (_, getters) => {
    if (getters['application/isArabic']) {
      return 'logo-tamara-ar'
    }

    return 'logo-tamara'
  },
  shouldDisplayInStorePaymentLinkLandingPage: (state, getters) => {
    const isAutoLogin = state.auth.loginSource === 'auto'
    const isViewedPaymentLinkLandingPage =
      state.checkout.viewedPaymentLinkLandingPage
    const fromPaymentLink = getters['session/fromPaymentLink']

    return isAutoLogin && !isViewedPaymentLinkLandingPage && fromPaymentLink
  },
  shouldDisplayCheckboxTermAndConditions: (_, getters) => {
    const termsAccepted = getters['auth/currentCustomer']?.termsAccepted
    const supportedCountries = ['SA', 'AE']

    return (
      !termsAccepted &&
      supportedCountries.includes(getters['session/countryCode'])
    )
  },
  shouldDisplayComponentV5(_, getters) {
    return (
      getters['session/singleCheckoutRevampEnabled'] ||
      getters['session/isPayNowOrder']
    )
  },
  canMakePaymentsWithApplePay(_, getters) {
    const countryCode = getters['session/countryCode']
    const paymentType = getters['session/paymentType']

    // is apple pay allowed in this country?
    // Param name: "apple_pay_enabled"
    const isEnabledApplePay = getters['configuration/isEnabledApplePay']

    // Should this user be allowed to use Apple Pay? (from Credit Risk)
    const applePayPreference = getters['payment/applePayPreference']

    return (
      isApplePayAvailable(countryCode, paymentType) &&
      isEnabledApplePay &&
      applePayPreference // fix PMO-302
    )
  },
  applePayStatus(_, getters) {
    const countryCode = getters['session/countryCode']
    const paymentType = getters['session/paymentType']
    const isEnabledApplePay = getters['configuration/isEnabledApplePay']
    const applePayPreference = getters['payment/applePayPreference']
    return {
      isApplePayAvailable: isApplePayAvailable(countryCode, paymentType),
      isEnabledApplePay,
      applePayPreference,
    }
  },
  orderId(_, getters) {
    return getters['checkout/orderId'] || getters['session/orderId']
  },
  isEnabledCashback(_, getters) {
    return (
      getters['session/cashbackEnabled'] && getters['session/isPayNowOrder']
    )
  },
  isSkipIdVerificationForInstalment(_, getters) {
    const userCheckoutConfig = getters['configuration/userCheckoutConfig']
    const isIdVerified = getters['auth/isIdVerified']
    const currentStep = getters['checkout/currentStep']
    const isSkippedNafath = getters['checkout/isSkippedNafath']
    const isInstalmentConfirmationStep =
      currentStep === CHECKOUT_STEPS.pay_by_instalment_confirmation
    const isRiskConsultStep =
      currentStep === CHECKOUT_STEPS.risk_consult_response_handling
    const isChargeDownPaymentStep =
      currentStep === CHECKOUT_STEPS.pay_by_instalment_charge_down_payment
    const isFinishStep = currentStep === CHECKOUT_STEPS.FINISH

    const configItem = userCheckoutConfig?.find(
      (config) => config.paymentType === PAYMENT_TYPES.PAY_BY_INSTALMENTS
    )

    const isSkipIdVerification = configItem?.isSkipIdVerification ?? false

    // This handles the cases where BE forces the customer ID verfication even if the customer is already verfied. (Only for KSA)
    const isCustomerVerfied =
      isIdVerified && currentStep !== CHECKOUT_STEPS.id_verification_sau

    return (
      isSkipIdVerification ||
      isCustomerVerfied ||
      isInstalmentConfirmationStep ||
      isSkippedNafath ||
      isRiskConsultStep ||
      isChargeDownPaymentStep ||
      isFinishStep
    )
  },
  failureRedirectUrl(_, getters) {
    return (
      getters['session/redirectUrl'] ||
      getters['checkout/merchantFailureUrl'] ||
      ''
    )
  },
  payWithCardType(_, getters) {
    const payViaApplePay = getters['instalment/payViaApplePay']
    const isEnabledApplePay = getters['configuration/isEnabledApplePay']
    const hasSavedCardsNotBlacklisted =
      getters['payment/hasSavedCardsNotBlacklisted']
    const isAddingNewCard = getters['instalment/isAddingNewCard']

    if (payViaApplePay && isEnabledApplePay) {
      return PAYMENT_CARD_TYPES.APPLE_PAY
    }

    if (!hasSavedCardsNotBlacklisted || isAddingNewCard) {
      return PAYMENT_CARD_TYPES.NEW_CARD
    }

    return PAYMENT_CARD_TYPES.EXISTING_CARD
  },
}

export default getters
